import React, { useCallback, useEffect, useRef, useState } from 'react';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Chip, CircularProgress, IconButton, InputBase, Stack, Typography, useTheme } from '@mui/material';

import { transcribeAudio } from '~/services/AIChatService';

type VoiceState = 'idle' | 'recording' | 'transcribing';

const HAS_MEDIA_DEVICES = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
const WAVEFORM_HISTORY_SIZE = 64;
const WAVEFORM_BAR_GAP = 2;
const WAVEFORM_SAMPLE_INTERVAL_MS = 40;

function RecordingWaveform({ stream }: { stream: MediaStream }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const barColor = theme.palette.primary.main;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return undefined;
    }

    const AudioCtx =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) {
      return undefined;
    }

    let audioCtx: AudioContext;
    let source: MediaStreamAudioSourceNode;
    let analyser: AnalyserNode;
    try {
      audioCtx = new AudioCtx();
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
    } catch {
      return undefined;
    }

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const history: Array<number> = new Array(WAVEFORM_HISTORY_SIZE).fill(0);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      audioCtx.close().catch(() => undefined);
      return undefined;
    }

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const cssWidth = container.clientWidth || 1;
      const cssHeight = container.clientHeight || 1;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    let frameId = 0;
    let lastSampleAt = 0;
    const draw = (timestamp: number) => {
      if (timestamp - lastSampleAt >= WAVEFORM_SAMPLE_INTERVAL_MS) {
        analyser.getByteTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
          const normalized = (dataArray[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / bufferLength);
        const amplitude = Math.min(rms * 5, 1);

        history.push(amplitude);
        if (history.length > WAVEFORM_HISTORY_SIZE) {
          history.shift();
        }
        lastSampleAt = timestamp;
      }

      const width = container.clientWidth;
      const height = container.clientHeight;
      ctx.clearRect(0, 0, width, height);

      const slot = width / WAVEFORM_HISTORY_SIZE;
      const barWidth = Math.max(slot - WAVEFORM_BAR_GAP, 1);
      const centerY = height / 2;

      ctx.fillStyle = barColor;
      for (let i = 0; i < history.length; i++) {
        const value = history[i];
        const barHeight = Math.max(value * (height - 4), 2);
        const x = i * slot;
        const y = centerY - barHeight / 2;
        const radius = barWidth / 2;
        ctx.beginPath();
        const ctxWithRoundRect = ctx as CanvasRenderingContext2D & {
          roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
        };
        if (typeof ctxWithRoundRect.roundRect === 'function') {
          ctxWithRoundRect.roundRect(x, y, barWidth, barHeight, radius);
        } else {
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }

      frameId = requestAnimationFrame(draw);
    };
    frameId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        // ignore
      }
      audioCtx.close().catch(() => undefined);
    };
  }, [stream, barColor]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minWidth: 0,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        px: 0.5,
        position: 'relative',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </Box>
  );
}

interface AiChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isLoading: boolean;
  selectionOptions?: Array<string>;
  selectionLabel?: string | null;
}

export default function AiChatInput({
  onSend,
  onStop,
  isLoading,
  selectionOptions = [],
  selectionLabel = null,
}: AiChatInputProps) {
  const [input, setInput] = useState('');
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const hasSelectionOptions = selectionOptions.length > 0;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Array<Blob>>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) {
      return;
    }
    onSend(trimmed);
    setInput('');
  }, [input, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleSelectionClick = useCallback(
    (option: string) => {
      if (isLoading) {
        return;
      }
      onSend(`My department is ${option}.`);
    },
    [isLoading, onSend],
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecordingStream(null);
  }, []);

  const handleTranscription = useCallback(async (blob: Blob) => {
    setVoiceState('transcribing');
    setVoiceError(null);
    try {
      const text = await transcribeAudio(blob);
      if (text) {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
      }
    } catch {
      setVoiceError('Transcription failed. Please try again.');
    } finally {
      setVoiceState('idle');
    }
  }, []);

  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setRecordingStream(stream);
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        stopStream();
        handleTranscription(blob);
      };

      recorder.start();
      setVoiceState('recording');
    } catch {
      stopStream();
      setVoiceError('Microphone access denied.');
      setVoiceState('idle');
    }
  }, [stopStream, handleTranscription]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (voiceState === 'recording') {
      stopRecording();
    } else if (voiceState === 'idle') {
      startRecording();
    }
  }, [voiceState, startRecording, stopRecording]);

  const micDisabled = isLoading || voiceState === 'transcribing';
  const sendDisabled = !input.trim() || isLoading || hasSelectionOptions;
  const actionDisabled = isLoading ? !onStop : sendDisabled;
  const handleActionClick = isLoading && onStop ? onStop : handleSend;
  const isRecording = voiceState === 'recording';

  return (
    <Box sx={{ px: 2, pt: 1, pb: 2 }}>
      {hasSelectionOptions && (
        <>
          {selectionLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {selectionLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {selectionOptions.map((option) => (
              <Chip
                key={option}
                label={option}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => handleSelectionClick(option)}
                disabled={isLoading}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Stack>
        </>
      )}

      {voiceError && (
        <Typography variant="caption" color="error" sx={{ mb: 0.5, display: 'block' }}>
          {voiceError}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minHeight: 44,
          px: 1.5,
          py: 0.75,
          borderRadius: 6,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: isRecording ? 'primary.main' : 'divider',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'border-color 120ms ease-in-out, box-shadow 120ms ease-in-out',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1f`,
          },
        }}
      >
        {isRecording && recordingStream ? (
          <RecordingWaveform stream={recordingStream} />
        ) : (
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            placeholder={hasSelectionOptions ? 'Choose a department above...' : 'Message the assistant'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || hasSelectionOptions}
            sx={{
              flex: 1,
              fontSize: '0.9rem',
              py: 0.5,
              px: 0.5,
              alignSelf: 'center',
            }}
          />
        )}

        {HAS_MEDIA_DEVICES && (
          <IconButton
            size="small"
            onClick={handleMicClick}
            disabled={micDisabled}
            color={isRecording ? 'error' : 'default'}
          >
            {voiceState === 'transcribing' ? (
              <CircularProgress size={18} />
            ) : isRecording ? (
              <StopIcon fontSize="small" />
            ) : (
              <MicIcon fontSize="small" />
            )}
          </IconButton>
        )}

        <IconButton
          onClick={handleActionClick}
          disabled={actionDisabled}
          size="small"
          sx={{
            bgcolor: isLoading ? 'error.main' : 'primary.main',
            color: 'primary.contrastText',
            width: 32,
            height: 32,
            '&:hover': { bgcolor: isLoading ? 'error.dark' : 'primary.dark' },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {isLoading ? <StopIcon sx={{ fontSize: 18 }} /> : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
    </Box>
  );
}
